import { useEffect, useState } from "react";
import { Button, Col, Form, InputGroup, Modal, Row } from "react-bootstrap";
import { CancelButton, SubmitButton } from "../../components/btns";
import { errorAlert, successAlert } from "../../components/toastr";
import { createFeedbackQuestion, updateFeedbackQuestion } from "../../resources/api/events";

const emptyQuestion = { question: "", question_type: "rating", allow_other: true, options: ["", ""] };

const QuestionForm = ({ eventId, show, setShow, question, onSave }) => {
    const [details, setDetails] = useState(emptyQuestion);
    const [validated, setValidated] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const action = question?.id ? "edit" : "create";

    useEffect(() => {
        if (show) setDetails({
            ...emptyQuestion,
            ...(question || {}),
            options: question?.options?.length ? question.options.map(option => typeof option === "string" ? option : option.label) : ["", ""]
        });
    }, [question, show]);

    const change = e => {
        const { name, value } = e.currentTarget;
        setDetails(details => ({ ...details, [name]: value }));
    };

    const changeOption = (index, value) => setDetails(current => ({
        ...current,
        options: current.options.map((option, optionIndex) => optionIndex === index ? value : option)
    }));
    const addOption = () => setDetails(current => ({ ...current, options: [...current.options, ""] }));
    const removeOption = index => setDetails(current => ({ ...current, options: current.options.filter((_, optionIndex) => optionIndex !== index) }));

    const submit = e => {
        e.preventDefault();
        const options = details.options.map(option => option.trim()).filter(Boolean);
        const uniqueOptions = new Set(options.map(option => option.toLowerCase()));
        const invalidOptions = options.length < 2 || uniqueOptions.size !== options.length || uniqueOptions.has("other");
        if (!e.currentTarget.checkValidity() || (details.question_type === "options" && invalidOptions)) {
            setValidated(true);
            return errorAlert(details.question_type === "options" ? "Enter at least two unique options. ‘Other’ is reserved for the switch." : "Enter the feedback question.", "Form Errors");
        }
        setSubmitting(true);
        const data = {
            question: details.question,
            question_type: details.question_type,
            allow_other: details.question_type === "options" && details.allow_other ? 1 : 0,
            options: details.question_type === "options" ? options : []
        };
        const request = action === "edit"
            ? updateFeedbackQuestion(eventId, details.id, data)
            : createFeedbackQuestion(eventId, data);
        request
            .then(({ question: savedQuestion, message }) => {
                successAlert(message);
                onSave(savedQuestion);
                setShow(false);
                setValidated(false);
            })
            .catch(errorAlert)
            .finally(() => setSubmitting(false));
    };

    return (
        <Modal show={show} onHide={() => setShow(false)} centered animation={false} backdrop="static">
            <Modal.Header closeButton className="bg-secondary text-light"><Modal.Title>{action === "edit" ? "Edit Question" : "Add Question"}</Modal.Title></Modal.Header>
            <Form noValidate validated={validated} onSubmit={submit}>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label className="form-field-title">Question</Form.Label>
                        <Form.Control as="textarea" rows={3} name="question" value={details.question} onChange={change} required />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label className="form-field-title">Answer Type</Form.Label>
                        <Form.Select name="question_type" value={details.question_type} onChange={change} required>
                            <option value="rating">Rating (1–5)</option>
                            <option value="textarea">Text response</option>
                            <option value="options">Choose an option</option>
                        </Form.Select>
                    </Form.Group>
                    {details.question_type === "options" && (
                        <div className="mt-3">
                            <Form.Label className="form-field-title">Answer Options</Form.Label>
                            <Row className="g-2">
                                {details.options.map((option, index) => (
                                    <Col xs={12} key={index}>
                                        <InputGroup>
                                            <InputGroup.Text>{index + 1}</InputGroup.Text>
                                            <Form.Control value={option} maxLength={255} required onChange={e => changeOption(index, e.currentTarget.value)} placeholder="Option label" />
                                            <Button type="button" variant="outline-danger" disabled={details.options.length <= 2} onClick={() => removeOption(index)} aria-label={`Remove option ${index + 1}`}><i className="fas fa-times" /></Button>
                                        </InputGroup>
                                    </Col>
                                ))}
                            </Row>
                            <Button type="button" variant="link" size="sm" className="px-0 mt-2" onClick={addOption}><i className="fas fa-plus-circle me-1" />Add another option</Button>
                            <Form.Check
                                className="mt-2"
                                type="switch"
                                id="allow-other-response"
                                label="Allow an ‘Other’ response"
                                checked={!!details.allow_other}
                                onChange={e => {
                                    const checked = e.currentTarget.checked;
                                    setDetails(current => ({ ...current, allow_other: checked }));
                                }}
                            />
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="py-1">
                    <SubmitButton type="submit" isSubmitting={submitting}>{action === "edit" ? "Update" : "Add Question"}</SubmitButton>
                    <CancelButton type="button" isSubmitting={submitting} onClick={() => setShow(false)}>Cancel</CancelButton>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default QuestionForm;
